import type { Pfm } from '@aviata/platform-client';
import { computed, isRef, type MaybeRef, unref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { isNil } from 'lodash-es';

import type { ProductName } from '../types';

/**
 * @example
 * {
 *    total_cashback_percent: 13.0,
 *    limit_per_transaction: 20000,
 *    limit_per_month: 150000,
 *    received_cashback_sum: 3382.84,
 *    found: true
 *    level_name: 'Platinum'
 *    cashback_percents: [
 *     {
 *       name: "базового кешбэка за уровень лояльности Platinum",
 *       percentage: 4.0
 *     },
 *     {
 *       name: "выполненного задания «Покупки»",
 *       percentage: 0.0
 *     },
 *     {
 *       name: "кешбэка за оплату в приложении",
 *       percentage: 4.0
 *     },
 *     {
 *       name: "повышенного кешбэка от партнера",
 *       percentage: 5.0
 *     }
 *   ]
 * }
 * @description
 * - `total_cashback_percent` - процент кешбэка (приходит в зависимости от уровня в ПЛ Фридом)
 * - `limit_per_transaction` - лимит за транзакцию (приходит в зависимости от уровня в ПЛ Фридом)
 * - `limit_per_month` - лимит в месяц (приходит в зависимости от уровня в ПЛ Фридом)
 * - `received_cashback_sum` - сумма кешбэков за текущий месяц
 * - `found` - клиент банка или не клиент
 * - `level_name` - уровень клиента в программе лояльности
 * - `cashback_percents` - из чего состоит `total_cashback_percent`
 */
export interface FreedomCashback {
  total_cashback_percent: number;
  limit_per_transaction: number;
  limit_per_month: number;
  received_cashback_sum: number;
  found: boolean;
  level_name: string;
  cashback_percents: Array<{
    name: string;
    percentage: number;
  }>;
}

export function useFreedomCashbackQuery({
  pfm,
  billId,
  productName,
  enabled: externalEnabled,
}: {
  pfm: Pfm;
  billId?: MaybeRef<string | undefined>;
  productName?: ProductName;
  enabled?: MaybeRef<boolean>;
}) {
  return useQuery({
    queryKey: ['freedom-cashback', 'internal-cashback', billId],
    queryFn: async () => {
      const resolvedBillId = unref(billId);

      const url = resolvedBillId
        ? `/freedom-cashback/internal-cashback/${resolvedBillId}`
        : '/freedom-cashback/internal-cashback';
      const urlWithProductName = productName
        ? `${url}?product_name=${productName}`
        : url;

      const response = await pfm.client.get(urlWithProductName);
      return response.data as FreedomCashback;
    },
    enabled: computed(() => {
      const billIdReady = isRef(billId) ? !isNil(billId.value) : true;
      const externalReady = !isNil(externalEnabled)
        ? unref(externalEnabled)
        : true;
      return billIdReady && externalReady;
    }),
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
